import React, { useState, useEffect, useRef } from 'react';

// A single streak of light that sweeps down a panel's left border, round the
// bottom-left corner and along the bottom, then waits and repeats.
// Drop inside a position:relative panel.
const TOTAL = 100; // path is normalised to this length via pathLength

// The tail is many faint, equal-width layers of increasing length stacked on
// each other, so opacity builds up smoothly towards the head (no visible steps).
const TAIL_LENGTH = 30;
const TAIL_STEPS = 14;
const LAYERS = [
  // [length, width, opacity, linecap]
  ...Array.from({ length: TAIL_STEPS }, (_, i) => [TAIL_LENGTH * (i + 1) / TAIL_STEPS, 1.5, 0.14, 'butt']),
  [2.5, 2, 1, 'round'], // bright head
];

export default function BorderStreak({ color = '#23a55a', radius = 12, every = 8, sweep = 1.4 }) {
  const ref = useRef(null);
  const [size, setSize] = useState(null);

  // Match the panel's size so the path sits exactly on its border
  useEffect(() => {
    const panel = ref.current?.parentElement;
    if (!panel) return;
    const measure = () => setSize({ w: panel.offsetWidth, h: panel.offsetHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  const w = size?.w || 0;
  const h = size?.h || 0;
  const r = radius - 0.5; // border centre line
  const path = `M 0.5 0 L 0.5 ${h - radius} A ${r} ${r} 0 0 0 ${radius} ${h - 0.5} L ${w} ${h - 0.5}`;

  // Every layer's leading edge moves together from the path start to just past the end
  const travel = TOTAL + TAIL_LENGTH;
  const sweepPct = (sweep / every) * 100;
  const keyframes = LAYERS.map(([len], i) => `
    @keyframes borderStreak${i} {
      0% { stroke-dashoffset: ${len}; }
      ${sweepPct}% { stroke-dashoffset: ${len - travel}; }
      100% { stroke-dashoffset: ${len - travel}; }
    }`).join('');

  return (
    <svg
      ref={ref}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      style={{ position: 'absolute', top: 0, left: -1, pointerEvents: 'none', overflow: 'visible' }}
    >
      <style>{`
        ${keyframes}
        @media (prefers-reduced-motion: reduce) { .border-streak { display: none; } }
      `}</style>
      {size && (
        <g style={{ filter: `drop-shadow(0 0 2px ${color}) drop-shadow(0 0 5px ${color})` }}>
          {LAYERS.map(([len, width, opacity, cap], i) => (
            <path
              key={i}
              className="border-streak"
              d={path}
              pathLength={TOTAL}
              fill="none"
              stroke={color}
              strokeWidth={width}
              strokeOpacity={opacity}
              strokeLinecap={cap}
              strokeDasharray={`${len} ${TOTAL * 3}`}
              strokeDashoffset={len}
              style={{ animation: `borderStreak${i} ${every}s ease-in-out infinite` }}
            />
          ))}
        </g>
      )}
    </svg>
  );
}
